import { useGetTrConfig } from "features/technical-data/scenario/api/hooks";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { findLatestJobConfigId } from "../lib/findLatestJobConfigId";
import { mapTrConfigToFormState } from "../lib/mapTrConfigToFormState";
import { useTransientConfiguration } from "./useTransientConfiguration";
import { useTransientProcessingJobs } from "./useTransientProcessingJobs";
import { useTransientScenarios } from "./useTransientScenarios";

export const useTransientRequestForm = () => {
  const configuration = useTransientConfiguration();
  const scenarios = useTransientScenarios();
  const { isLoading: isJobsLoading, jobs, projectId } = useTransientProcessingJobs();
  const {
    getPayload: getConfigurationPayload,
    replace,
    reset: resetConfiguration,
    updateConfiguration,
  } = configuration;
  const { getPayload: getScenariosPayload, reset: resetScenarios } = scenarios;
  const lastConfigId = useMemo(() => findLatestJobConfigId(jobs), [jobs]);
  const lastConfigQuery = useGetTrConfig(projectId, lastConfigId);
  const initializedRef = useRef(false);

  const scenarioIdsKey = scenarios.scenarios.map((scenario) => scenario.id).join(",");
  const scenarioIds = useMemo(
    () => (scenarioIdsKey ? scenarioIdsKey.split(",").map(Number) : []),
    [scenarioIdsKey]
  );
  const waitingForLastConfig = Boolean(lastConfigId) && lastConfigQuery.isLoading;

  useEffect(() => {
    if (initializedRef.current || !scenarioIds.length || isJobsLoading || waitingForLastConfig) return;
    initializedRef.current = true;
    if (lastConfigQuery.data) {
      replace(mapTrConfigToFormState(lastConfigQuery.data, scenarioIds));
      return;
    }
    updateConfiguration("scenarios", scenarioIds);
  }, [isJobsLoading, lastConfigQuery.data, replace, scenarioIds, updateConfiguration, waitingForLastConfig]);

  useEffect(() => {
    if (!initializedRef.current || !scenarioIds.length) return;
    updateConfiguration("scenarios", scenarioIds);
  }, [scenarioIds, updateConfiguration]);

  const reset = useCallback(() => {
    initializedRef.current = false;
    resetScenarios();
    resetConfiguration(scenarioIds);
  }, [resetConfiguration, resetScenarios, scenarioIds]);

  const getPayload = useCallback(() => getConfigurationPayload(), [getConfigurationPayload]);

  return useMemo(
    () => ({
      configuration,
      getPayload,
      getScenariosPayload,
      reset,
      scenarios,
    }),
    [configuration, getPayload, getScenariosPayload, reset, scenarios]
  );
};

export type UseTransientRequestFormReturn = ReturnType<typeof useTransientRequestForm>;
